import React, { useEffect, useState, useMemo } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
  Box, Typography, Button, Paper, Grid, Card, CardContent, Avatar, 
  Skeleton, Alert, Stack, TableContainer, Table, TableHead, TableRow, 
  TableCell, TableBody 
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PieChartIcon from '@mui/icons-material/PieChart';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import SimpleTransactionForm from '../components/SimpleTransactionForm';
import RantingTransactionTabs from '../components/RantingTransactionTabs';
import RantingTransactionTable from '../components/RantingTransactionTable';
import useRantingTransactionStore from '../store/useRantingTransactionStore';
import useNotificationStore from '../store/useNotificationStore';
import useAuthStore from '../store/useAuthStore';

// --- Widget Component (Konsisten dengan Admin) ---
const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 64, height: 64, mr: 2.5 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={100} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const RantingDashboard = () => {
  const { user } = useAuthStore();
  const isLapangan = user?.jabatan === 'LAPANGAN';

  const {
    transactions,
    loading,
    error,
    fetchTransactions,
    addPenerimaanInfak,
    addPenerimaanDanaAmil,
    addPenerimaanZakat,
    addPenyaluran,
    addBebanPengelolaan,
    updateTransaction,
    removeTransaction,
  } = useRantingTransactionStore();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // --- Statistics Calculation ---
  const stats = useMemo(() => {
    if (!transactions) return { pemasukan: 0, pengeluaran: 0, saldo: 0, count: 0 };
    
    // Filter transaksi bulan ini (Opsional, saat ini kita hitung total semua untuk saldo real)
    // Untuk dashboard operasional, biasanya saldo akumulasi lebih relevan.
    
    return transactions.reduce((acc, tx) => {
        const amount = parseFloat(tx.jumlah);
        if (tx.jenis === 'PEMASUKAN') acc.pemasukan += amount;
        else acc.pengeluaran += amount;
        acc.count++;
        return acc;
    }, { pemasukan: 0, pengeluaran: 0, count: 0 });
  }, [transactions]);

  const saldo = stats.pemasukan - stats.pengeluaran;

  // --- Chart Data (Income by Category) ---
  const chartData = useMemo(() => {
    if (!transactions) return [];
    
    const incomeTx = transactions.filter(t => t.jenis === 'PEMASUKAN');
    const grouped = incomeTx.reduce((acc, tx) => {
        const cat = tx.kategori?.nama || 'Lainnya';
        acc[cat] = (acc[cat] || 0) + parseFloat(tx.jumlah);
        return acc;
    }, {});

    return Object.keys(grouped).map((key, index) => ({
        id: index,
        value: grouped[key],
        label: key,
        color: `hsl(${120 + (index * 40)}, 70%, 45%)` // Greenish hues for income
    }));
  }, [transactions]);


  // --- Handlers (DEFINED HERE) ---
  const handleAddOrUpdate = async (transactionData) => {
    try {
      if (editingTransaction) {
        await updateTransaction(editingTransaction.id, transactionData);
        showNotification('Transaksi berhasil diperbarui', 'success');
      } else {
        switch (activeTab) {
          case 0: await addPenerimaanInfak(transactionData); break;
          case 1: await addPenerimaanDanaAmil(transactionData); break;
          case 2: await addPenerimaanZakat(transactionData); break;
          case 3: await addPenyaluran(transactionData); break;
          case 4: await addBebanPengelolaan(transactionData); break;
          default: throw new Error('Invalid transaction type');
        }
        showNotification(`Transaksi ${editingTransaction ? 'diperbarui' : 'ditambahkan'}`, 'success');
      }
      setShowForm(false);
      setEditingTransaction(null);
      fetchTransactions(); // Re-fetch to update dashboard
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      showNotification(`Gagal menyimpan: ${errorMessage}`, 'error');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Hapus transaksi ini?')) {
      try {
        await removeTransaction(id);
        showNotification('Transaksi dihapus', 'success');
        fetchTransactions(); // Re-fetch to update dashboard
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        showNotification(`Gagal menghapus: ${errorMessage}`, 'error');
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingTransaction(null);
  };


  if (error) return <MainLayout><Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box></MainLayout>;

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box sx={{ mb: {xs: 2, md: 0} }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                    Dashboard Ranting
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isLapangan 
                    ? 'Mode Petugas Lapangan: Fokus pada pencatatan transaksi harian.' 
                    : `Selamat Datang, ${user?.username || 'Pengurus'}. Berikut ringkasan keuangan unit Anda.`}
                </Typography>
            </Box>

            {/* Quick Action Button */}
            <Button 
                variant="contained" 
                color="success" 
                size="large" 
                startIcon={<AddIcon />} 
                onClick={() => { setEditingTransaction(null); setShowForm(true); }}
                sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold', boxShadow: 3 }}
            >
                Input Transaksi Baru
            </Button>
        </Paper>

        {/* SECTION 1: HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
                <StatWidget title="Total Pemasukan" value={`Rp${stats.pemasukan.toLocaleString('id-ID')}`} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatWidget title="Total Pengeluaran" value={`Rp${stats.pengeluaran.toLocaleString('id-ID')}`} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatWidget title="Saldo Unit" value={`Rp${saldo.toLocaleString('id-ID')}`} icon={<AccountBalanceWalletIcon fontSize="large" />} color="#1976d2" loading={loading} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatWidget title="Total Transaksi" value={stats.count} icon={<SwapHorizIcon fontSize="large" />} color="#ed6c02" loading={loading} />
            </Grid>
        </Grid>

        {/* SECTION 2: ANALYSIS & ACTIONS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* LEFT: Pie Chart Analysis (Only show if data exists) */}
            <Grid item xs={12} md={8}>
                <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 3 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <PieChartIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Komposisi Pemasukan</Typography>
                        </Stack>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ height: 200, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    {loading ? <Skeleton variant="circular" width={180} height={180} /> : 
                                     (stats.pemasukan === 0 ? <Typography color="text.secondary">Belum ada data pemasukan.</Typography> :
                                        <PieChart series={[{ data: chartData, innerRadius: 40, outerRadius: 90, paddingAngle: 2, cornerRadius: 4 }]} width={250} height={200} slotProps={{ legend: { hidden: true } }} />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {!loading && stats.pemasukan > 0 && (
                                    <Stack spacing={2}>
                                        {chartData.map((d) => (
                                            <Box key={d.id} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                                    <Box sx={{ width: 12, height: 12, bgcolor: d.color, borderRadius: '50%' }} />
                                                    <Typography variant="body2" color="text.secondary">{d.label}</Typography>
                                                </Stack>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {((d.value / stats.pemasukan) * 100).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Rp{d.value.toLocaleString('id-ID')}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT: Quick Summary / Recent Activity */}
            <Grid item xs={12} md={4}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} /> Aktivitas Terbaru
                        </Typography>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Ket</TableCell>
                                        <TableCell align="right">Jml</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell><Skeleton/></TableCell><TableCell><Skeleton/></TableCell></TableRow>) : 
                                        transactions.slice(0, 5).map((tx) => (
                                            <TableRow key={tx.id}>
                                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                                    {tx.kategori?.nama || 'Umum'} <br/>
                                                    <Typography variant="caption" color="text.secondary">{new Date(tx.tanggal).toLocaleDateString('id-ID')}</Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: tx.jenis === 'PEMASUKAN' ? 'success.main' : 'error.main' 
                                                }}>
                                                    {tx.jenis === 'PEMASUKAN' ? '+' : '-'} {parseFloat(tx.jumlah).toLocaleString('id-ID')}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                    {transactions.length === 0 && <TableRow><TableCell colSpan={2} align="center">Belum ada aktivitas.</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

        {/* SECTION 3: TRANSACTION MANAGEMENT (Form & Full Table) */}
        {showForm ? (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
              {editingTransaction ? (
                // Use SimpleTransactionForm for editing specific details
                <SimpleTransactionForm
                  title="Edit Transaksi"
                  initialData={editingTransaction}
                  onSubmit={handleAddOrUpdate}
                  onCancel={handleCancelForm}
                  type={editingTransaction.jenis === 'PEMASUKAN' ? 'penerimaan' : 'penyaluran'}
                />
              ) : (
                // Use RantingTransactionTabs for adding new transactions
                <RantingTransactionTabs
                  onSubmit={handleAddOrUpdate}
                  onCancel={handleCancelForm}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isLapangan={isLapangan}
                />
              )}
            </Paper>
        ) : (
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Data Transaksi Lengkap</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingTransaction(null); setShowForm(true); }}>
                            Input Baru
                        </Button>
                    </Box>
                    <RantingTransactionTable
                        transactions={transactions}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </CardContent>
            </Card>
        )}

      </Box>
    </MainLayout>
  );
};

export default RantingDashboard;